import React from "react";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Building2,
  Check,
  Clock3,
  FileCheck2,
} from "lucide-react";

const acceptedInputs = [
  "ข้อความ LINE / แชตลูกความ",
  "อีเมลและไฟล์แนบ",
  "PDF สัญญา / เอกสารคดี",
  "รูปถ่ายหลักฐาน",
  "Excel รายชื่อลูกความและเคส",
];

const outputCards = [
  ["สร้างแฟ้มคดี", "แยกลูกความ คู่กรณี ข้อเท็จจริง หลักฐาน และงานถัดไป"],
  ["ร่างเอกสาร", "หนังสือทวงถาม สัญญา checklist และร่างเอกสารจากข้อมูลในเคส"],
  ["ค้นกฎหมายพร้อมอ้างอิง", "ตอบพร้อมมาตรา ฎีกา และแหล่งอ้างอิงที่ทีมกดตรวจต่อได้"],
  ["ตามงานและสรุปสถานะ", "แจ้งว่างานไหนเสร็จ งานไหนค้าง และใครต้องทำอะไรต่อ"],
];

const steps = [
  ["1", "ส่งข้อมูลให้ KHORN", "ข้อความลูกความ, PDF, รูปถ่าย, Excel"],
  ["2", "AI อ่านและแยกข้อมูล", "ลูกความ คู่กรณี ข้อเท็จจริง หลักฐาน"],
  ["3", "จัดเข้าแฟ้มคดี", "สร้างหรือจับคู่กับเคสเดิม"],
  ["4", "พร้อมให้ทนายตรวจ", "ร่างเอกสาร ค้นกฎหมาย ทำ checklist"],
];

export default function LandingPageSME() {
  return (
    <div className="min-h-screen bg-white text-[#111111] selection:bg-[#111111] selection:text-white">
      <Helmet>
        <title>KHORN AI SME Landing Test | AI สำหรับสำนักงานกฎหมายไทย</title>
        <meta
          name="description"
          content="หน้า Landing Page ทดลองสไตล์ SME สำหรับ KHORN AI ช่วยรับข้อมูลลูกความ จัดแฟ้มคดี ร่างเอกสาร ค้นกฎหมาย และตามงาน"
        />
        <link rel="canonical" href="https://www.khorn-ai.com/landingpageSME" />
      </Helmet>

      <header className="relative z-40 bg-white">
        <div className="mx-auto flex h-[76px] max-w-[1480px] items-center justify-between px-6 md:px-10">
          <a href="/" className="flex items-center gap-3">
            <MascotFace size="sm" />
            <div className="text-[30px] font-black tracking-[-0.04em] text-[#12172A]">KHORN</div>
          </a>

          <nav className="hidden items-center gap-14 text-[17px] font-semibold text-[#222222] lg:flex">
            <a href="#features">ฟีเจอร์</a>
            <a href="#how">ทำงานยังไง</a>
            <a href="#start">เริ่มต้นยังไง</a>
            <a href="#pricing">แพ็กเกจ</a>
            <a href="#faq">คำถามที่พบบ่อย</a>
          </nav>

          <a
            href="https://app.khorn-ai.com/plan"
            className="rounded-lg bg-[#111111] px-7 py-3.5 text-[16px] font-bold text-white shadow-[0_3px_0_rgba(0,0,0,0.2)]"
          >
            เข้าใช้งาน
          </a>
        </div>
      </header>

      <main className="overflow-hidden">
        <section className="relative min-h-[780px] pt-24 md:pt-32">
          <ReceiptDoodle className="absolute -left-16 top-[210px] hidden w-[270px] -rotate-[24deg] lg:block" />
          <PaperDoodle className="absolute -right-10 top-[270px] hidden w-[330px] rotate-[18deg] lg:block" />
          <Squiggle className="absolute -left-14 bottom-[74px] hidden h-[230px] w-[420px] text-[#67E948] lg:block" />
          <Squiggle className="absolute -right-24 top-[80px] hidden h-[280px] w-[360px] rotate-[28deg] text-[#D8DE39] lg:block" />

          <div className="relative mx-auto max-w-[1180px] px-5 text-center">
            <div className="relative mx-auto max-w-[1040px]">
              <h1 className="text-[48px] font-black leading-[1.05] tracking-[-0.06em] md:text-[82px] lg:text-[98px]">
                จัดแฟ้มคดีลูกความ
                <br />
                3 ชั่วโมงเสร็จได้ใน 3 นาที
              </h1>
              <MascotFace
                size="hero"
                className="absolute top-[52%] right-10 hidden -translate-y-1/2 md:inline-flex lg:-right-5"
              />
            </div>
            <p className="mx-auto mt-10 max-w-[760px] text-[22px] font-medium leading-9 text-[#686868]">
              KHORN ช่วยสำนักงานกฎหมายรับข้อมูลลูกความ อ่านเอกสาร จัดเป็นแฟ้มคดี ร่างเอกสาร
              และตามงานให้ทีมทำต่อได้เร็วกว่าเดิม
            </p>
            <div className="mt-10 flex justify-center">
              <a
                href="https://app.khorn-ai.com/plan"
                className="inline-flex items-center gap-2 rounded-lg bg-[#111111] px-8 py-4 text-[18px] font-bold text-white shadow-[0_4px_0_rgba(0,0,0,0.18)]"
              >
                ใช้งานเลย
                <ArrowRight size={20} />
              </a>
            </div>
          </div>

          <div className="relative mx-auto mt-24 grid max-w-[1180px] grid-cols-1 gap-8 px-5 text-center md:grid-cols-3">
            <Stat icon={<Building2 size={42} />} color="#4D75F0" value="2,000+" label="นักกฎหมายที่ใช้งาน" unit="คน" />
            <Stat icon={<FileCheck2 size={42} />} color="#F2A235" value="50+" label="ทีมกฎหมายและสำนักงาน" unit="ทีม" />
            <Stat icon={<Clock3 size={42} />} color="#55B778" value="10,000+" label="เวลางานเอกสารที่ประหยัดได้" unit="นาที" />
          </div>
        </section>

        <section id="features" className="relative bg-white py-20 md:py-28">
          <CameraDoodle className="absolute right-[12%] top-[22%] hidden w-[150px] text-[#88D56F] lg:block" />
          <div className="mx-auto grid max-w-[1180px] items-center gap-16 px-5 md:grid-cols-[0.9fr_1.1fr]">
            <PhoneMockup />

            <div>
              <h2 className="max-w-[720px] text-[46px] font-black leading-[1.12] tracking-[-0.06em] md:text-[68px]">
                เพียงแค่..ส่งข้อมูลลูกความมาให้ KHORN อ่าน
              </h2>
              <div className="mt-9 space-y-5">
                {acceptedInputs.map((item) => (
                  <div key={item} className="flex items-center gap-5 text-[25px] font-bold text-[#666666]">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#86C978] text-white">
                      <Check size={28} strokeWidth={4} />
                    </span>
                    {item}
                  </div>
                ))}
              </div>
              <p className="mt-8 max-w-[720px] text-[23px] font-semibold leading-9 text-[#666666]">
                นอกจากนี้ ยังอ่านได้ทั้ง <span className="text-[#4C5EDB]">ไฟล์ PDF, รูปถ่าย</span> หรือจะพิมพ์
                <span className="text-[#4C5EDB]"> “ขอร่างหนังสือทวงถาม”</span> ระบบก็เตรียมร่างแรกให้ตรวจต่อได้
              </p>
            </div>
          </div>
        </section>

        <section id="how" className="bg-[#F8FBF4] py-20">
          <div className="mx-auto max-w-[1180px] px-5">
            <SectionTitle
              title="จากข้อมูลกระจัดกระจาย กลายเป็นงานพร้อมทำต่อ"
              text="ออกแบบให้ทีม SME ใช้ได้ทันที ไม่ต้องเปลี่ยนวิธีทำงานทั้งสำนักงานตั้งแต่วันแรก"
            />
            <div className="mt-10 grid gap-5 md:grid-cols-4">
              {steps.map(([num, title, text]) => (
                <article key={num} className="rounded-[30px] border border-[#E2E7DD] bg-white p-7">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-[#111111] text-lg font-black text-white">{num}</div>
                  <h3 className="mt-8 text-2xl font-black tracking-[-0.04em]">{title}</h3>
                  <p className="mt-3 text-lg leading-8 text-[#676767]">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20">
          <div className="mx-auto max-w-[1180px] px-5">
            <SectionTitle
              title="KHORN ไม่ใช่แค่แชตบอท แต่เป็นคนช่วยงานคดี"
              text="ระบบช่วยเตรียมสิ่งที่ทำให้งานคดีเดินต่อได้จริง ทั้งแฟ้มคดี เอกสาร อ้างอิง และการตามงาน"
            />
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {outputCards.map(([title, text], index) => (
                <article key={title} className="rounded-[34px] border border-[#E2E7DD] bg-white p-8 shadow-[0_18px_60px_rgba(17,17,17,0.05)]">
                  <span className="text-sm font-black text-[#86C978]">0{index + 1}</span>
                  <h3 className="mt-5 text-4xl font-black tracking-[-0.05em]">{title}</h3>
                  <p className="mt-4 text-xl leading-9 text-[#676767]">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="start" className="bg-[#F8FBF4] py-20">
          <div className="mx-auto max-w-[1180px] px-5 text-center">
            <h2 className="text-[48px] font-black leading-tight tracking-[-0.05em] md:text-[76px]">
              เริ่มจากงานที่ปวดหัวที่สุดก่อน
            </h2>
            <p className="mx-auto mt-6 max-w-[760px] text-[22px] leading-9 text-[#676767]">
              ส่งเอกสารลูกความชุดแรกให้ KHORN อ่าน แล้วดูว่าเคส งาน และเอกสารถูกจัดออกมาอย่างไร
            </p>
            <a
              href="https://app.khorn-ai.com/plan"
              className="mt-10 inline-flex items-center gap-2 rounded-lg bg-[#111111] px-8 py-4 text-[18px] font-bold text-white"
            >
              เริ่มใช้งาน
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        <section id="pricing" className="bg-white py-20">
          <div className="mx-auto max-w-[1180px] px-5">
            <SectionTitle title="แพ็กเกจเรียบง่าย" text="เริ่มใช้กับงานจริงก่อน แล้วค่อยเลือกแพ็กเกจตามจำนวนคนในทีม" />
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              <Plan name="Elite" price="429" href="https://app.khorn-ai.com/signup?plan=elite&commitment=monthly" />
              <Plan name="Team" price="729" href="https://app.khorn-ai.com/signup?plan=team&commitment=monthly" featured />
              <Plan name="Custom" price="คุยกับเรา" href="https://app.khorn-ai.com/booking" />
            </div>
          </div>
        </section>

        <section id="faq" className="bg-[#F8FBF4] py-20">
          <div className="mx-auto max-w-[980px] px-5">
            <SectionTitle title="คำถามที่พบบ่อย" />
            <div className="mt-10 space-y-4">
              <Faq q="ต้องใช้ LINE เท่านั้นไหม?" a="ไม่จำเป็น ใช้ได้ทั้งเว็บแอป อีเมล ไฟล์ PDF รูปถ่าย และ Excel ส่วน LINE เป็นหนึ่งในช่องทางรับข้อมูล" />
              <Faq q="AI ใช้แทนทนายได้ไหม?" a="ไม่ได้ KHORN เป็นผู้ช่วยเตรียมงานและข้อมูล ผลลัพธ์ต้องให้ทนายหรือทีมกฎหมายตรวจเสมอ" />
              <Faq q="ข้อมูลลูกความปลอดภัยไหม?" a="KHORN ออกแบบสำหรับงานกฎหมาย มี no-training policy และควบคุมสิทธิ์การเข้าถึงตามทีม" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#E2E7DD] bg-white py-8">
        <div className="mx-auto flex max-w-[1180px] flex-col gap-3 px-5 text-sm font-semibold text-[#676767] md:flex-row md:items-center md:justify-between">
          <span>KHORN AI Solution</span>
          <span>AI สำหรับสำนักงานกฎหมายไทย</span>
        </div>
      </footer>
    </div>
  );
}

function MascotFace({ size = "sm", className = "" }) {
  const sizeClass = size === "hero" ? "h-[72px] w-[72px] md:h-[92px] md:w-[92px]" : "h-11 w-11";
  return (
    <span className={`${sizeClass} ${className} relative shrink-0 items-center justify-center rounded-xl bg-[#87D1EF] shadow-[inset_0_8px_0_#92D760]`}>
      <span className="absolute left-[25%] top-[48%] h-2 w-3 rounded-full bg-[#12172A]" />
      <span className="absolute right-[25%] top-[48%] h-2 w-3 rounded-full bg-[#12172A]" />
      <span className="absolute left-1/2 top-[61%] h-2 w-3 -translate-x-1/2 rounded-b-full border-b-2 border-[#12172A]" />
    </span>
  );
}

function Stat({ icon, color, value, label, unit }) {
  return (
    <div className="border-[#EEF0EB] md:border-l md:first:border-l-0">
      <div className="mx-auto mb-4 flex justify-center" style={{ color }}>
        {icon}
      </div>
      <p className="text-[18px] font-bold text-[#8A8F8A]">{label}</p>
      <p className="mt-3 text-[52px] font-black leading-none tracking-[-0.05em]" style={{ color }}>
        {value}
      </p>
      <p className="mt-4 text-[17px] font-semibold text-[#A3A7A3]">{unit}</p>
    </div>
  );
}

function PhoneMockup() {
  return (
    <div className="mx-auto w-full max-w-[350px] rounded-[54px] border border-[#E6E8E2] bg-white p-2 shadow-[0_30px_90px_rgba(50,70,50,0.12)]">
      <div className="rounded-[48px] border border-[#D9DED5] bg-[#F8FAF7] p-3">
        <div className="rounded-[40px] bg-white p-3">
          <div className="flex items-center justify-between text-xs font-black">
            <span>16:30</span>
            <span>4G 94</span>
          </div>
          <div className="mt-4 flex items-center gap-2 border-b border-[#E7E7E7] pb-3">
            <span className="text-lg font-black">‹ 99+</span>
            <MascotFace size="sm" />
            <div>
              <p className="text-sm font-black">KHORN</p>
              <p className="text-xs text-[#8A8A8A]">จัดแฟ้มคดีอัตโนมัติ</p>
            </div>
          </div>
          <div className="min-h-[420px] bg-[#9BB4D8] p-4">
            <div className="ml-auto max-w-[240px] rounded-[22px] rounded-tr-md bg-[#7EE66D] px-4 py-3 text-sm font-bold">
              ส่งแชตลูกความกับสัญญาให้จัดเป็นเคส
            </div>
            <div className="mt-5 flex gap-2">
              <MascotFace size="sm" />
              <div className="max-w-[235px] rounded-[22px] rounded-tl-md bg-white px-4 py-3 text-sm font-semibold leading-6">
                อ่านแล้วค่ะ สร้างแฟ้ม “ผิดสัญญาจ้างบริการ” พร้อมสรุปคู่กรณี หลักฐาน และงานถัดไปให้แล้ว
              </div>
            </div>
            <div className="mt-5 rounded-[20px] bg-white p-4">
              <p className="text-xs font-black text-[#87C978]">KHORN</p>
              <p className="mt-2 text-xl font-black">แฟ้มคดีใหม่</p>
              <div className="mt-3 space-y-2 text-sm text-[#555555]">
                <p>ลูกความ: บริษัท ABC จำกัด</p>
                <p>ประเภท: ผิดสัญญาจ้างบริการ</p>
                <p>งานถัดไป: ร่างหนังสือทวงถาม</p>
              </div>
              <button className="mt-4 w-full rounded-lg bg-[#19A14A] py-3 text-sm font-bold text-white">เปิดแฟ้ม</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Plan({ name, price, href, featured = false }) {
  return (
    <article className={`rounded-[34px] border p-8 ${featured ? "border-[#111111] bg-[#111111] text-white" : "border-[#E2E7DD] bg-white"}`}>
      <h3 className="text-3xl font-black">{name}</h3>
      <div className="mt-8 text-5xl font-black tracking-[-0.06em]">{price === "คุยกับเรา" ? price : `฿${price}`}</div>
      {price !== "คุยกับเรา" && <p className={`mt-2 text-base ${featured ? "text-white/65" : "text-[#676767]"}`}>ต่อเดือน</p>}
      <a className={`mt-8 inline-flex h-13 w-full items-center justify-center rounded-lg py-4 text-base font-bold ${featured ? "bg-white text-[#111111]" : "bg-[#111111] text-white"}`} href={href}>
        เลือกแพ็กเกจ
      </a>
    </article>
  );
}

function Faq({ q, a }) {
  return (
    <article className="rounded-[24px] border border-[#E2E7DD] bg-white p-7">
      <h3 className="text-2xl font-black">{q}</h3>
      <p className="mt-3 text-lg leading-8 text-[#676767]">{a}</p>
    </article>
  );
}

function SectionTitle({ title, text }) {
  return (
    <div className="mx-auto max-w-[900px] text-center">
      <h2 className="text-[46px] font-black leading-tight tracking-[-0.05em] md:text-[70px]">{title}</h2>
      {text && <p className="mx-auto mt-5 max-w-[760px] text-[21px] leading-9 text-[#676767]">{text}</p>}
    </div>
  );
}

function ReceiptDoodle({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 220 310" fill="none" aria-hidden="true">
      <path d="M25 18 195 46 169 292l-20-18-25 13-19-19-26 12-17-21-27 8L25 18Z" stroke="#0D1021" strokeWidth="5" />
      <path d="M58 74h86M54 101h112M50 128h92M47 155h100M43 184h75" stroke="#0D1021" strokeWidth="4" strokeLinecap="round" />
      <path d="M57 218h66M52 242h78" stroke="#0D1021" strokeWidth="4" strokeLinecap="round" strokeDasharray="3 10" />
    </svg>
  );
}

function PaperDoodle({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 340 260" fill="none" aria-hidden="true">
      <path d="M83 77 241 18l67 130-160 82L83 77Z" stroke="#0D1021" strokeWidth="5" />
      <path d="M106 126 8 178l92 63 79-47" stroke="#0D1021" strokeWidth="5" />
      <path d="M132 92h97M147 119h87M162 146h91M64 185h84M82 207h59" stroke="#0D1021" strokeWidth="4" strokeLinecap="round" />
      <path d="M246 68c-18 10-25 27-15 40 12 16 44 10 47-12 2-17-13-31-32-28Z" stroke="#0D1021" strokeWidth="4" />
    </svg>
  );
}

function Squiggle({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 460 260" fill="none" aria-hidden="true">
      <path
        d="M9 137c73-113 145-117 113 12-19 74-78 106-101 63-25-47 63-146 138-134 76 12 3 135-77 119-60-12 11-99 94-75 107 32 117 153-22 127"
        stroke="currentColor"
        strokeWidth="9"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CameraDoodle({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 180 120" fill="none" aria-hidden="true">
      <path d="M28 38h29l9-16h50l10 16h26v62H28V38Z" stroke="currentColor" strokeWidth="5" strokeLinejoin="round" />
      <circle cx="91" cy="70" r="24" stroke="currentColor" strokeWidth="5" />
      <path d="M82 58c15-8 29 4 25 20M43 51h17" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
    </svg>
  );
}
